import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useCardLinks } from "../../../../mobile/ui/screens/cards/components/use-card-links";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../shared/core/logging/logger";
import { cardManager, featuresManager } from "../../../../shared/core/service/services";
import { getAvailableBalance } from "../../../../shared/domains/account/account";
import { useAccounts } from "../../../../shared/domains/account/use-accounts";
import { CardStatus } from "../../../../shared/domains/cards/card";
import type { OverrideCardData } from "../../../../shared/domains/cards/card-service";
import { useCardOperation, useCards } from "../../../../shared/domains/cards/use-cards";
import { getWalletCard } from "../../../../shared/domains/cards/wallet-card";
import { useProducts } from "../../../../shared/domains/products/use-products";
import { isDefined } from "../../../../shared/utils/assert";
import { useObservable } from "../../../../shared/utils/observable";
import { CARD_SENSITIVE_DETAILS_MODAL_ID } from "../../../core/modal/modal-id";
import { ActionRowList } from "../../common/action-row";
import { BluePrimaryButton } from "../../common/buttons/primary-button";
import { Modal } from "../../common/modal/modal";
import { PageHeaderBand, PageHeaderSubtitle, PageHeaderTitle } from "../../common/page/page-header-band";
import { MainColorSpinner } from "../../common/spinner";
import { StaticAssets } from "../../styles/custom-assets";
import { theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";
import { ActionMenu } from "./components/action-menu";
import { EmptyCardPanel } from "./components/card-creation/empty-card-panel";
import { CardSensitiveDetails } from "./components/card-sensitive-detail";
import { CardVisual } from "./components/card-visual";
import { AdditionalInfosSection } from "./components/infos/additional-infos";
import { ActivationButton } from "./components/options/activation-button";
import { CardFeaturesSwitches } from "./components/options/card-features-switches";
import { DeactivateButton } from "./components/options/deactivate-button";
import { LockCardSwitch } from "./components/options/lock-card-switch";
import { OppositionButton } from "./components/options/opposition-button";
import { OrderNewCardButton } from "./components/options/order-new-card";
import { RefabricateButton } from "./components/options/refabricate-button";
import { UpdateCardPinCodeButton } from "./components/pincode/update-card-pincode-button";

/**
 * The "Ma carte" screen: the holder's card and every setting it carries. A holder owns a single
 * card, so the card is shown on its own, as it is on mobile.
 */
export function AllCardsScreen() {
  const { cards, loading: cardsLoading, error: cardsError, retry: reloadCards } = useCards();
  const { loading: productsLoading, products } = useProducts();
  const accounts = useAccounts();
  const features = useObservable(featuresManager.features);
  const { formatMessage } = useIntl();
  const { canRefabricateCard } = useCardLinks();

  const [sensitiveData, setSensitiveData] = useState<OverrideCardData | null>(null);
  const [pendingOperation, setPendingOperation] = useState(false);

  const screenLoading = cardsLoading || productsLoading;
  const card = useMemo(() => getWalletCard(cards), [cards]);
  const { loading, oppose, deactivate, block, unblock, enableFeature } = useCardOperation(card?.id ?? null);

  useEffect(() => {
    setSensitiveData(null);
    setPendingOperation(isDefined(card?.pendingManagementOperation));
  }, [card]);

  const balance = useMemo(() => {
    const account = accounts.find((a) => a.id === card?.accountId);
    return account ? getAvailableBalance(account) : null;
  }, [accounts, card]);

  const hasActions = useMemo(() => {
    switch (card?.status) {
      case CardStatus.Pending:
      case CardStatus.Active:
      case CardStatus.Blocked:
        return true;
      default:
        return false;
    }
  }, [card]);

  const canActivateCard = useMemo(() => {
    const cardProduct = products.find((p) => p.id === card?.productId);
    return isDefined(cardProduct?.cardActivationMode);
  }, [card, products]);

  // The settings column exists only for a card that actually carries switchable features.
  const showFeatures =
    !!card &&
    (card.status === CardStatus.Active || card.status === CardStatus.Blocked) &&
    (card.features?.length ?? 0) > 0;

  const canOrderAgain = !!card && (card.status === CardStatus.Opposed || canRefabricateCard(card));

  const refreshCard = useCallback(async () => {
    if (isDefined(card)) {
      try {
        await cardManager.retrieveCard(card.id);
      } catch (e) {
        logger.debug("Error refreshCard : ", e?.response?.data?.error?.message);
      }
    }
  }, [card]);

  const toggleCardDetails = () => {
    if (sensitiveData?.panDisplay) {
      setSensitiveData(null);
    } else {
      Modal.present(
        CARD_SENSITIVE_DETAILS_MODAL_ID,
        () => <CardSensitiveDetails card={card} onCompleted={(res) => setSensitiveData(res)} />,
        { canBeDismissed: false },
      );
    }
  };

  return (
    <>
      <PageHeaderBand>
        <PageHeaderTitle>{formatMessage("cardsList.myCardTitle")}</PageHeaderTitle>
        <PageHeaderSubtitle>{formatMessage("cardsList.myCardSubtitle")}</PageHeaderSubtitle>
      </PageHeaderBand>
      {screenLoading ? (
        <LoadingContainer>
          <MainColorSpinner />
        </LoadingContainer>
      ) : cardsError ? (
        <LoadError>
          <LoadErrorMessage>{formatMessage("cardsList.loadCardsError")}</LoadErrorMessage>
          <RetryButton size="XS" onClick={reloadCards}>
            {formatMessage("cardsList.loadCardsRetry")}
          </RetryButton>
        </LoadError>
      ) : !card ? (
        <EmptyCardPanel />
      ) : (
        <Columns>
          <MainColumn>
            <CardVisual card={card} balance={balance} sensitiveData={sensitiveData} />
            <StyledAdditionalInfos card={card} onClick={refreshCard} />
            {(hasActions || canOrderAgain) && (
              <Actions>
                {hasActions && (
                  <>
                    <LockCardSwitch
                      card={card}
                      loading={loading}
                      disabled={pendingOperation}
                      block={block}
                      unblock={unblock}
                    />
                    <UpdateCardPinCodeButton card={card} disabled={pendingOperation} />
                    {card.virtual ? (
                      <DeactivateButton card={card} loading={loading} deactivate={deactivate} />
                    ) : (
                      <OppositionButton card={card} loading={loading} oppose={oppose} />
                    )}
                    {canActivateCard && <ActivationButton card={card} disabled={pendingOperation} />}
                    {/* The limits are out of the redesign's scope; the row comes back with them. */}
                    {features.cardSensitiveDataView && (
                      <ActionMenu
                        title={formatMessage(
                          sensitiveData?.panDisplay
                            ? "cardOptions.switch.hideDetails"
                            : "cardOptions.switch.showDetails",
                        )}
                        iconSrc={StaticAssets.cardDetails}
                        onClick={toggleCardDetails}
                      />
                    )}
                  </>
                )}
                {card.status === CardStatus.Opposed && <OrderNewCardButton card={card} />}
                {canRefabricateCard(card) && card.status !== CardStatus.Opposed && <RefabricateButton card={card} />}
              </Actions>
            )}
          </MainColumn>
          {showFeatures && (
            <SideColumn>
              <SectionTitle>{formatMessage("cardOptions.title")}</SectionTitle>
              <CardFeaturesSwitches
                card={card}
                loading={loading}
                enableFeature={enableFeature}
                disabled={pendingOperation}
              />
            </SideColumn>
          )}
        </Columns>
      )}
    </>
  );
}

const LoadingContainer = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: center;
  padding: 80px 0;
`;

/* A failed list is not an empty one: the holder is told, and offered the call again. */
const LoadError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: 20px;
  padding: 64px 24px;
  border-radius: 12px;
  background-color: ${theme.colors.blue[200]};
`;

const LoadErrorMessage = styled.span`
  ${theme.text};
  text-align: center;
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const RetryButton = styled(BluePrimaryButton)`
  padding: 13px 28px;
  box-shadow: none;
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  align-self: stretch;
  width: 100%;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex-direction: column;
    gap: 24px;
  }
`;

/* The card sets the column's width, so it keeps the mockup's proportions on any window. */
const MainColumn = styled.div`
  display: flex;
  flex: 0 0 472px;
  flex-direction: column;
  min-width: 0;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex: 1 1 auto;
    align-self: stretch;
  }
`;

const SideColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    align-self: stretch;
  }
`;

const StyledAdditionalInfos = styled(AdditionalInfosSection)`
  margin-top: 20px;
`;

const Actions = styled(ActionRowList)`
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  ${theme.mediumText};
  margin: 0 0 12px;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;
